import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../../components/ui/dialog";
import { Label } from "../../../components/ui/label";
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import { Checkbox } from "../../../components/ui/checkbox";
import { useDropdownData } from "../../Services/Common";
import Multiselect from "multiselect-react-dropdown";
import { useCatalogueManagement } from "../../Services/CatalogueManagement";
import { toast } from "react-toastify";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../../components/ui/sheet"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select"
import { Textarea } from "../../../components/ui/textarea"
import {
  Package,
  Loader2,
  X,
  Plus
} from "lucide-react"
import { inputFieldValidator } from "../../../Utilities/validation";
import { isApiError, getApiErrorMessage } from "../../../Utilities/apiResponse";
import type { HierarchyNode, FieldType, MetaDataField } from "./Types"
import { useProductDesignManagement } from "../../Services/ProductDesignManagement"
import DynamicFieldForm from "./DynamicsField"

const findNodeById = (nodes: HierarchyNode[], targetId: string): HierarchyNode | null => {
  for (const node of nodes) {
    if (node.id === targetId) return node;
    if (node.children?.length) {
      const found = findNodeById(node.children, targetId);
      if (found) return found;
    }
  }
  return null;
};

const findHierarchyPath = (
  roots: HierarchyNode[],
  targetId: string
): { node1Id: string; node2Id: string; node3Id: string } | null => {
  const walk = (nodes: HierarchyNode[], path: HierarchyNode[]): HierarchyNode[] | null => {
    for (const node of nodes) {
      const nextPath = [...path, node];
      if (node.id === targetId) return nextPath;
      if (node.children?.length) {
        const found = walk(node.children, nextPath);
        if (found) return found;
      }
    }
    return null;
  };

  const path = walk(roots, []);
  if (!path?.length) return null;

  const node1 = path.find((n) => n.nodeType === 1);
  const node2 = path.find((n) => n.nodeType === 2);
  const node3 = path.find((n) => n.nodeType === 3) ?? path[path.length - 1];

  return {
    node1Id: node1?.id ?? "",
    node2Id: node2?.id ?? "",
    node3Id: node3?.id ?? targetId,
  };
};

const mergeHierarchyWithSavedFields = (
  hierarchyFields: MetaDataField[],
  savedFields: MetaDataField[]
): { mergedFields: MetaDataField[]; selectedForSubmit: MetaDataField[] } => {
  const savedById = new Map(savedFields.map((s) => [s.id, s]));

  const mergedFields = hierarchyFields.map((hf) => {
    const saved = savedById.get(hf.id);
    if (!saved) return hf;
    if (hf.isSystemField) return hf;
    return {
      ...hf,
      fieldName: saved.fieldName ?? hf.fieldName,
      fieldDisplayName: saved.fieldDisplayName ?? hf.fieldDisplayName,
      fieldDescription: saved.fieldDescription ?? hf.fieldDescription,
      isRequiredField: saved.isRequiredField ?? hf.isRequiredField,
      isSendToBlockChain: saved.isSendToBlockChain ?? hf.isSendToBlockChain,
    };
  });

  const selectedForSubmit = mergedFields.filter(
    (f) => f.isSystemField || savedById.has(f.id)
  );

  return { mergedFields, selectedForSubmit };
};

const AddProductDesignDialog = ({
  isOpen,
  onClose,
  selectedProductType,
  onSuccessCallBack,
  isUpdate,
  setIsUpdate
}) => {

  const [fieldTypes, setFieldTypes] = useState<FieldType[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { getMetaDataFieldTypes, getTenantHierarchy, createMetaDataField, updateMetaDataField } = useProductDesignManagement();
  const { createCatalogueType, updateCatalogueType } = useCatalogueManagement();
  // Form state
  const [selectedNode1, setSelectedNode1] = useState("")
  const [selectedNode2, setSelectedNode2] = useState("")
  const [selectedNode3, setSelectedNode3] = useState("")
  const [selectedNode4, setSelectedNode4] = useState("")
  const [fields, setFields] = useState<MetaDataField[]>([])
  const [selectedFields, setSelectedFields] = React.useState<any[]>([]);

  // Derived state for cascading dropdowns
  const [node2Options, setNode2Options] = useState<HierarchyNode[]>([])
  const [node3Options, setNode3Options] = useState<HierarchyNode[]>([])
  const [node4Options, setNode4Options] = useState<HierarchyNode[]>([])

  const [hierarchyData, setHierarchyData] = useState<HierarchyNode[]>([]);
  const prevSelectedNode1 = useRef("");
  const prevSelectedNode2 = useRef("");
  const editHydratedKeyRef = useRef<string | null>(null);

  const [attributes, setAttributes] = useState({
    code: undefined,
    description: undefined,
    catalogName: undefined
  });

  const [errors, setErrors] = useState({
    code: false,
    catalogName: false,
    description: false,
    hierarchy: false,
    fields: false
  });

  useEffect(() => {
    getTenantHierarchy().then((data) => {
      setHierarchyData(data);
    }).catch(() => {
      setHierarchyData([]);
    })

    getMetaDataFieldTypes().then((data) => {
      setFieldTypes(data);
    }).catch(() => {
      setFieldTypes([]);
    });
  }, []);

  // Update node2 options when node1 changes (only reset children when user changes parent)
  useEffect(() => {
    if (selectedNode1) {
      const node1 = hierarchyData.find((n) => n.id === selectedNode1);
      setNode2Options(node1?.children || []);
      if (prevSelectedNode1.current && prevSelectedNode1.current !== selectedNode1) {
        setSelectedNode2("");
        setSelectedNode3("");
        setSelectedNode4("");
        setFields([]);
        setSelectedFields([]);
      }
      prevSelectedNode1.current = selectedNode1;
    } else {
      setNode2Options([]);
      prevSelectedNode1.current = "";
    }
  }, [selectedNode1, hierarchyData]);

  // Update node3 options when node2 changes
  useEffect(() => {
    if (selectedNode2) {
      const node2 =
        node2Options.find((n) => n.id === selectedNode2) ||
        findNodeById(hierarchyData, selectedNode2);
      setNode3Options(node2?.children || []);
      if (prevSelectedNode2.current && prevSelectedNode2.current !== selectedNode2) {
        setSelectedNode3("");
        setSelectedNode4("");
        setFields([]);
        setSelectedFields([]);
      }
      prevSelectedNode2.current = selectedNode2;
    } else {
      setNode3Options([]);
      prevSelectedNode2.current = "";
    }
  }, [selectedNode2, node2Options, hierarchyData]);

  // Update node4 options when node3 changes
  useEffect(() => {
    if (selectedNode3) {
      const node3 =
        node3Options.find((n) => n.id === selectedNode3) ||
        findNodeById(hierarchyData, selectedNode3);
      setNode4Options(node3?.children || []);
    } else {
      setNode4Options([]);
    }
  }, [selectedNode3, node3Options, hierarchyData]);

  const resetFormState = () => {
    prevSelectedNode1.current = "";
    prevSelectedNode2.current = "";
    editHydratedKeyRef.current = null;
    setSelectedNode1("");
    setSelectedNode2("");
    setSelectedNode3("");
    setSelectedNode4("");
    setNode2Options([]);
    setNode3Options([]);
    setNode4Options([]);
    setFields([]);
    setSelectedFields([]);
    setAttributes({
      catalogName: undefined,
      code: undefined,
      description: undefined,
    });
    setErrors({
      code: false,
      catalogName: false,
      description: false,
      hierarchy: false,
      fields: false,
    });
    setError(null);
    setLoading(false);
    setIsUpdate(false);
  };

  useEffect(() => {
    if (!isOpen) {
      resetFormState();
      return;
    }
    if (!isUpdate) {
      resetFormState();
    }
  }, [isOpen, isUpdate]);

  useEffect(() => {
    if (!isOpen || !isUpdate || !selectedProductType?.fields || hierarchyData.length === 0) {
      return;
    }

    const hydrateKey = `${selectedProductType.id}`;
    if (editHydratedKeyRef.current === hydrateKey) {
      return;
    }
    editHydratedKeyRef.current = hydrateKey;

    try {
      const parsedFields = JSON.parse(selectedProductType.fields);
      const savedMetaFields: MetaDataField[] = parsedFields?.metaDataFields || [];
      const productTypeNodeId = parsedFields?.id;

      setAttributes({
        code: selectedProductType?.code,
        description: selectedProductType?.description,
        catalogName: selectedProductType?.catalogName,
      });

      const ancestors = findHierarchyPath(hierarchyData, productTypeNodeId);
      const node1Id = ancestors?.node1Id ?? "";
      const node2Id = ancestors?.node2Id ?? parsedFields?.parentHierarchyNodeId ?? "";
      const node3Id = ancestors?.node3Id ?? productTypeNodeId ?? "";

      const node1 = hierarchyData.find((n) => n.id === node1Id);
      const n2Options = node1?.children || [];
      const node2 =
        n2Options.find((n) => n.id === node2Id) || findNodeById(hierarchyData, node2Id);
      const n3Options = node2?.children || [];
      const node3 =
        n3Options.find((n) => n.id === node3Id) || findNodeById(hierarchyData, node3Id);

      const { mergedFields, selectedForSubmit } = mergeHierarchyWithSavedFields(
        node3?.metaDataFields || [],
        savedMetaFields
      );

      prevSelectedNode1.current = node1Id;
      prevSelectedNode2.current = node2Id;

      setNode2Options(n2Options);
      setNode3Options(n3Options);
      setSelectedNode1(node1Id);
      setSelectedNode2(node2Id);
      setSelectedNode3(node3Id);
      setFields(mergedFields);
      setSelectedFields(selectedForSubmit);
    } catch {
      toast.error("Failed to load product design for editing");
    }
  }, [isOpen, isUpdate, selectedProductType, hierarchyData]);

  const getFieldById = (fieldId: string) => fields.find((f) => f.id === fieldId);

  const buildMetaDataFieldsPayload = (): MetaDataField[] =>
    selectedFields.map((selected) => {
      const current = getFieldById(selected.id);
      if (!current) return selected;
      if (current.isSystemField) return selected;
      return {
        ...selected,
        fieldName: current.fieldName,
        fieldDisplayName: current.fieldDisplayName,
        fieldDescription: current.fieldDescription,
        isRequiredField: current.isRequiredField,
        isSendToBlockChain: current.isSendToBlockChain,
      };
    });

  const handleCheckboxChange = (field: MetaDataField, checked: boolean) => {
    const currentField = getFieldById(field.id) ?? field;
    if (checked) {
      setSelectedFields((prev) =>
        prev.some((f) => f.id === currentField.id) ? prev : [...prev, currentField]
      );
    } else {
      setSelectedFields((prev) => prev.filter((f) => f.id !== field.id));
    }
    setErrors((prev) => ({ ...prev, fields: false }));
  };

  const handleFieldChange = (index: number, updatedField: MetaDataField) => {
    const updatedFields = [...fields];
    updatedFields[index] = updatedField;
    setFields(updatedFields);

    setSelectedFields((prev) =>
      prev.map((f) => (f.id === updatedField.id ? { ...f, ...updatedField } : f))
    );
  };

  const handleNode3Data = (nodeId: string) => {
    const node3 = findNodeById(hierarchyData, nodeId);
    const hierarchyFields = node3?.metaDataFields || [];

    if (isUpdate && selectedFields.length > 0) {
      const { mergedFields, selectedForSubmit } = mergeHierarchyWithSavedFields(
        hierarchyFields,
        selectedFields
      );
      setFields(mergedFields);
      setSelectedFields(selectedForSubmit);
      return;
    }

    setFields(hierarchyFields);
    setSelectedFields(hierarchyFields.filter((field) => field.isSystemField === true));
  };

  const onChange = (e, type) => {
    const value = e.target.value;
    setAttributes((prev) => ({ ...prev, [type]: value }));
    setErrors((prev) => ({ ...prev, [type]: false }));
  };

  const validateForm = () => {
    const newErrors = {
      code: inputFieldValidator(attributes?.code, "string"),
      catalogName: inputFieldValidator(attributes?.catalogName, "string"),
      description: inputFieldValidator(attributes?.description, "string"),
      hierarchy: !selectedNode3,
      fields: selectedFields.length === 0,
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some((v) => v);
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error("Please fill in all required fields before saving");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const node3 = findNodeById(hierarchyData, selectedNode3);
      const nodeData = {
        ...node3,
        metaDataFields: buildMetaDataFieldsPayload(),
      };
      const data = {
        ...attributes,
        fields: nodeData
      };

      const response = isUpdate
        ? await updateCatalogueType({ id: selectedProductType?.id, ...data })
        : await createCatalogueType(data);

      if (isApiError(response)) {
        const message = getApiErrorMessage(response);
        toast.error(message);
        setError(message);
        setLoading(false);
        return;
      }

      toast.success(isUpdate ? "Product Design updated." : "Product Design added");
      onSuccessCallBack();
      setLoading(false);
      handleClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to create product design";
      toast.error(message);
      setError(message);
      setLoading(false);
    }
  };

  const handleClose = () => {
    resetFormState();
    onClose();
  };

  // Get node1 options (nodeType === 1)
  const node1Options = hierarchyData.filter((n) => n.nodeType === 1)

  return (
    <Sheet open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <SheetContent side="right" className="w-full min-w-[600px] p-0 flex flex-col h-full max-h-[100dvh]">
        <SheetHeader className="bg-gradient-to-r from-purple-50 to-blue-50 p-6 border-b detail-header shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-purple-100 p-2 rounded-lg">
                <Package className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <SheetTitle className="text-xl font-bold text-gray-900"> Add New Product Design</SheetTitle>
                <p className="text-sm text-gray-600 mt-1">Add catalogue type base on your product</p>
              </div>
            </div>
          </div>
          <Button variant="outline" onClick={handleClose} className="close-panel">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-x h-4 w-4"><path d="M18 6 6 18"></path><path d="m6 6 12 12"></path></svg>
          </Button>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto p-6 space-y-6 min-h-0">
          {error && (
            <div className="mb-4 p-3 bg-red-950 border border-red-800 rounded text-red-200 text-sm">{error}</div>
          )}

          {/* Product Design Basic Info */}
          <div className="space-y-4 mb-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="code" className="text-sm font-medium text-slate-200">
                  Code *
                </Label>
                <Input
                  id="code"
                  placeholder="Enter product code"
                  value={attributes?.code}
                  onChange={(e) => onChange(e, "code")}
                  disabled={loading}
                  className={`w-full ${errors.code ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                />
              </div>
              <div>
                <Label htmlFor="name" className="text-sm font-medium text-slate-200">
                  Name *
                </Label>
                <Input
                  id="name"
                  placeholder="Enter product name"
                  value={attributes?.catalogName}
                  onChange={(e) => onChange(e, "catalogName")}
                  disabled={loading}
                  className={`w-full ${errors.catalogName ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="description" className="text-sm font-medium text-slate-200">
                Description *
              </Label>
              <Textarea
                id="description"
                placeholder="Enter product description"
                value={attributes?.description}
                required={true}
                onChange={(e) => onChange(e, "description")}
                disabled={loading}
                rows={3}
                className={`w-full ${errors.description ? "border-red-500 focus-visible:ring-red-500" : ""}`}
              />
            </div>
          </div>

          {/* Hierarchy Dropdowns */}
          <div className="space-y-4 mb-6 rounded-lg">
            <h3 className="font-semibold text-sm text-white">Select Hierarchy</h3>

            <div>
              <Label htmlFor="node1" className="text-sm font-medium text-slate-200">
                Category
              </Label>
              <Select value={selectedNode1} onValueChange={setSelectedNode1} disabled={loading}>
                <SelectTrigger id="node1" className="w-full mt-2">
                  <SelectValue placeholder="Select Level 1" />
                </SelectTrigger>
                <SelectContent className="w-full">
                  {node1Options.map((node) => (
                    <SelectItem key={node.id} value={node.id} className="text-white">
                      {node.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedNode1 && node2Options.length > 0 && (
              <div>
                <Label htmlFor="node2" className="text-sm font-medium text-slate-200">
                  Sub Category
                </Label>
                <Select value={selectedNode2} onValueChange={setSelectedNode2} disabled={loading}>
                  <SelectTrigger id="node2" className="w-full mt-2">
                    <SelectValue placeholder="Select Level 2" />
                  </SelectTrigger>
                  <SelectContent className="w-full">
                    {node2Options.map((node) => (
                      <SelectItem key={node.id} value={node.id} className="text-white">
                        {node.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {selectedNode2 && node3Options.length > 0 && (
              <div>
                <Label htmlFor="node3" className="text-sm font-medium text-slate-200">
                  Product Type
                </Label>
                <Select
                  value={selectedNode3}
                  onValueChange={(event) => {
                    setSelectedNode3(event);
                    handleNode3Data(event);
                    setErrors((prev) => ({ ...prev, hierarchy: false, fields: false }));
                  }}
                  disabled={loading}
                >
                  <SelectTrigger id="node3" className={`w-full mt-2 ${errors.hierarchy ? "border-red-500 focus-visible:ring-red-500" : ""}`}>
                    <SelectValue placeholder="Select Level 3" />
                  </SelectTrigger>
                  <SelectContent className="w-full">
                    {node3Options.map((node) => (
                      <SelectItem key={node.id} value={node.id} className="text-white">
                        {node.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Dynamic Fields Section - Only show at nodeType 3 */}
          {(selectedNode3 || selectedNode4) && (
            <div className={`space-y-4 mb-6 ${errors.fields ? "rounded-lg border border-red-500 p-2" : ""}`}>
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-sm text-white">Dynamic Fields</h3>
              </div>

              <div className="mb-2">
                {fields.map((field, index) => (
                  <div key={index} className="p-4 mb-2 border border-slate-600 rounded-lg relative">
                    <Checkbox
                      checked={selectedFields.some((data) => data.id === field?.id)}
                      disabled={field.isSystemField}
                      className="absolute top-2 right-2"
                      onCheckedChange={(checked) => handleCheckboxChange(field, checked === true)}
                    />

                    <DynamicFieldForm
                      field={field}
                      fieldTypes={fieldTypes}
                      onChange={(updated) => handleFieldChange(index, updated)}
                      disabled={loading || field.isSystemField}
                      isShowValues={false}
                    />
                  </div>
                ))}

                {fields.length === 0 && (
                  <div className="p-4 text-center text-slate-400 text-sm border border-dashed border-slate-600 rounded-lg">
                    No fields added yet. Click "Add Field" to create one.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        <div className="shrink-0 px-6 py-4 border-t border-border bg-card flex justify-end gap-3">
          <Button
            type="submit"
            onClick={handleSubmit}
            disabled={loading}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {isUpdate ? ("Updating...") : ("Adding...")}
              </>
            ) : (
              isUpdate ? ("Update Product") : ("Add Product")
            )}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default AddProductDesignDialog;